import { Layers } from "@mui/icons-material"
import { Box, Button, CircularProgress, Grid2 as Grid, Paper, TextField } from "@mui/material"
import Stack from "@mui/material/Stack"
import { DataGrid, GridColDef, GridRowsProp } from "@mui/x-data-grid"
import { useMeasure } from "@uidotdev/usehooks"
import useModalTrigger from "magik-react-hooks/useModalTrigger"
import { useMemo } from "react"
import { useTranslation } from "react-i18next"
import { useNavigate } from "react-router-dom"
import CustomToolbar from "../components/CustomToolbar"
import DialogConfirm from "../components/DialogConfirm"
import DialogForm from "../components/DialogForm"
import IconButtonDelete from "../components/IconButtonDelete"
import Layout from "../components/Layout"
import { PageTitle } from "../components/PageTitle"
import Paginator from "../components/Paginator"
import SupportoForm from "../components/forms/SupportoForm"
import { SelectFormato } from "../components/forms/formsHelpers"
import { useQsFilters } from "../hooks/filters"
import useUserCanWriteAndDelete from "../hooks/permissions"
import { useSnackbarMessage } from "../hooks/snackbar"
import { useDownloadSupporti, useSupportiList, useSupportoCreate, useSupportoDelete } from "../hooks/supporti"
import { Supporto } from "../types"
import LangLink from "../components/LangLink"
import { useIsMobile } from "../hooks/mobile"
import filesaver from "file-saver"
import SupportiImport from "../components/SupportiImport"
import { useIsTablet } from "../hooks/tablet"
import { ACVocabolario, ACVocabolarioMulti } from "../components/forms/autocompletes"
import { useAuth } from "../auth"
import { PAGE_SIZE_OPTIONS, usePageSize } from "../hooks/pageSize"

const initFilters = (params: URLSearchParams) => ({
  page: Number(params.get("page") ?? 1),
  search: params.get("search") ?? "",
  ordering: params.get("ordering") ?? "segnatura",
  formato: params.get("formato") ?? "",
  elemento: params.get("elemento") ?? "",
  cronologia_da: params.get("cronologia_da") ?? "",
  cronologia_a: params.get("cronologia_a") ?? "",
  audio: params.get("audio") ?? "",
  tipo_supporto: params.get("tipo_supporto") ?? "",
  genere: (params.getAll("genere") as unknown[]) ?? [],
})

type SupportiProps = {
  fondoId?: number
  documentoId?: number
  assemblaggioId?: number
}

export default function Supporti({ fondoId, documentoId, assemblaggioId }: SupportiProps) {
  const pageSize = usePageSize()
  const { filters, setFilters, uiFilters, setFiltersDebounced } = useQsFilters(initFilters)
  const { t, i18n } = useTranslation()
  const navigate = useNavigate()
  const userCanWriteAndDelete = useUserCanWriteAndDelete()

  const { user } = useAuth()
  const isMobile = useIsMobile() || user?.display === "mobile"
  const isTablet = (useIsTablet() || user?.display === "tablet") && !isMobile

  const columns: GridColDef[] = useMemo(() => {
    let cols: GridColDef[] = []
    cols.push({
      field: "segnatura",
      headerName: t("supporto.segnatura"),
      minWidth: 200,
      renderCell: (params) => {
        // const toLink = documentoId ? `/documenti/${documentoId}/supporti/${params.row.id}` : `/supporti/${params.row.id}`
        const toLink = `/supporti/${params.row.id}`
        return (
          <LangLink to={toLink} style={{ color: "black" }}>
            {params.row?.segnatura}
          </LangLink>
        )
      },
    })
    if (isMobile) {
      return cols
    }

    cols.push({
      field: "titolo_proprio",
      headerName: t("supporto.titolo_proprio"),
      flex: 1,
    })

    if (isTablet) {
      if (userCanWriteAndDelete) {
        cols.push({
          field: "id",
          headerName: t("general.azioni"),
          flex: 1,
          display: "flex",
          align: "right",
          headerAlign: "right",
          hideSortIcons: true,
          sortable: false,
          renderCell: (params) => (
            <>
              <IconButtonDelete onClick={() => deleteModalActions.open(params.row)} />
            </>
          ),
        })
      }
      return cols
    }

    cols.push(
      {
        field: "formato_data",
        headerName: t("supporto.formato"),
        flex: 1,
        renderCell: (params) => params.row?.formato_data?.nome,
      },
      {
        field: "assemblaggio_data",
        headerName: t("supporto.assemblaggio"),
        flex: 1,
        sortable: false,
        renderCell: (params) =>
          params.value && (
            <LangLink to={`/assemblaggi/${params.value.id}`} style={{ color: "black" }}>
              {params.value.nome}
            </LangLink>
          ),
      },
      {
        field: "cronologia_da",
        headerName: t("fondo.cronologia_da"),
        flex: 1,
        renderCell: (params) => {
          return params.row.cronologia_da
        },
      },
      {
        field: "cronologia_a",
        headerName: t("fondo.cronologia_a"),
        flex: 1,
        renderCell: (params) => {
          return params.row.cronologia_a
        },
      },
      {
        field: "lunghezza",
        headerName: t("supporto.lunghezza"),
        flex: 1,
      },
      {
        field: "durata_presunta",
        headerName: t("supporto.durata_presunta"),
        flex: 1,
      },
    )
    if (userCanWriteAndDelete) {
      cols.push({
        field: "id",
        headerName: t("general.azioni"),
        flex: 1,
        display: "flex",
        align: "right",
        headerAlign: "right",
        hideSortIcons: true,
        sortable: false,
        renderCell: (params) =>
          userCanWriteAndDelete && <IconButtonDelete onClick={() => deleteModalActions.open(params.row)} />,
      })
    }
    return cols
  }, [t, userCanWriteAndDelete, isMobile, isTablet])

  const { data, isLoading } = useSupportiList({
    page: String(filters.page),
    page_size: String(pageSize),
    search: filters.search,
    ordering: filters.ordering,
    formato: filters.formato,
    elemento: filters.elemento,
    fondo: fondoId ? String(fondoId) : "",
    documento: documentoId ? String(documentoId) : "",
    assemblaggio: assemblaggioId ? String(assemblaggioId) : "",
    cronologia_da: filters.cronologia_da,
    cronologia_a: filters.cronologia_a,
    audio: filters.audio,
    tipo_supporto: filters.tipo_supporto,
    genere: filters.genere as string[],
  })

  const { mutateAsync: create } = useSupportoCreate()
  const { mutateAsync: remove } = useSupportoDelete()
  const { mutateAsync: downloadSupporti, isPending: isPendingSupporti } = useDownloadSupporti({
    search: filters.search,
    ordering: filters.ordering,
    formato: filters.formato,
    elemento: filters.elemento,
    fondo: fondoId ? String(fondoId) : "",
    documento: documentoId ? String(documentoId) : "",
    assemblaggio: assemblaggioId ? String(assemblaggioId) : "",
    cronologia_da: filters.cronologia_da,
    cronologia_a: filters.cronologia_a,
    audio: filters.audio,
    tipo_supporto: filters.tipo_supporto,
    genere: filters.genere as string[],
  })

  const numPages = useMemo(() => {
    if (!data) return 1
    return Math.ceil(data.count / pageSize)
  }, [data, pageSize])

  const [ref, { height }] = useMeasure()
  const [toolbarRef, { height: toolbarHeight }] = useMeasure()
  const tableHeight = useMemo(() => {
    if (!height || !toolbarHeight) return
    return height - toolbarHeight
  }, [height, toolbarHeight])

  const [createModalState, createModalActions] = useModalTrigger()
  const [deleteModalState, deleteModalActions] = useModalTrigger()

  const showMessage = useSnackbarMessage()

  const filtersCount = useMemo(() => {
    let count = 0
    if (filters.formato) count++
    if (filters.elemento) count++
    if (filters.cronologia_da) count++
    if (filters.cronologia_a) count++
    if (filters.audio) count++
    if (filters.tipo_supporto) count++
    if (filters.genere && (filters.genere as unknown[]).length > 0) count++

    return count
  }, [filters.formato, filters.elemento, filters.audio, filters.tipo_supporto, filters.genere])

  const [importModalState, importModalActions] = useModalTrigger()

  return (
    <Layout padding>
      <Stack sx={{ height: "100%" }}>
        {!documentoId && !fondoId && !assemblaggioId && (
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <PageTitle
              title={t("supporto.titolo_lista")}
              breadcrumbs={[
                {
                  label: "Supporti",
                  iconType: Layers,
                },
              ]}
            />
            <Box>
              {userCanWriteAndDelete && !isMobile && !fondoId && !documentoId && !assemblaggioId && (
                <Button
                  sx={{
                    marginRight: 1,
                  }}
                  variant="contained"
                  color="secondary"
                  disabled={isPendingSupporti}
                  onClick={() => {
                    downloadSupporti().then((blob) => {
                      filesaver.saveAs(blob, `supporti_${new Date().toISOString().split("T")[0]}.xlsx`)
                    })
                  }}
                >
                  {isPendingSupporti ? <CircularProgress size={16} color="inherit" sx={{ mr: 1 }} /> : null}
                  {t("supporto.download_excel")}
                </Button>
              )}
              {userCanWriteAndDelete && !isMobile && !fondoId && !documentoId && !assemblaggioId && (
                <Button
                  variant="contained"
                  color="secondary"
                  onClick={() => {
                    importModalActions.open()
                  }}
                  sx={{
                    marginRight: 1,
                  }}
                >
                  {t("supporto.cta_importa")}
                </Button>
              )}
              {userCanWriteAndDelete && !isMobile && (
                <Button
                  variant="contained"
                  color="primary"
                  onClick={() => {
                    createModalActions.open()
                  }}
                >
                  {t("supporto.cta_aggiungi")}
                </Button>
              )}
            </Box>
          </Box>
        )}
        {fondoId && userCanWriteAndDelete && !isMobile && (
          <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 2 }}>
            <Button
              variant="contained"
              color="primary"
              onClick={() => {
                createModalActions.open()
              }}
            >
              {t("supporto.cta_aggiungi")}
            </Button>
          </Box>
        )}
        <Box flexGrow={1}>
          <Paper sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
            <CustomToolbar
              count={data?.count}
              search={uiFilters.search}
              setSearch={(search) => setFiltersDebounced({ ...filters, search, page: 1 })}
              clearFilters={() =>
                setFilters({
                  search: "",
                  page: 1,
                  formato: "",
                  elemento: "",
                  audio: "",
                  tipo_supporto: "",
                  cronologia_a: "",
                  cronologia_da: "",
                  genere: [],
                })
              }
              filtersCount={filtersCount}
              ref={toolbarRef}
            >
              <Grid container spacing={2}>
                <Grid size={3}>
                  <SelectFormato
                    fullWidth
                    size="small"
                    margin="dense"
                    label={t("supporto.formato")}
                    vocabolario={"object_formato"}
                    value={filters.formato || ""}
                    onChange={(e) => setFilters({ ...filters, formato: e.target.value, page: 1 })}
                  />
                </Grid>
                <Grid size={3}>
                  <TextField
                    label={t("fondo.cronologia_da")}
                    type="number"
                    margin="dense"
                    variant="outlined"
                    size="small"
                    fullWidth
                    value={uiFilters.cronologia_da}
                    onChange={(e) => setFilters({ ...filters, cronologia_da: e.target.value, page: 1 })}
                  />
                </Grid>
                <Grid size={3}>
                  <TextField
                    label={t("fondo.cronologia_a")}
                    type="number"
                    margin="dense"
                    variant="outlined"
                    size="small"
                    fullWidth
                    value={uiFilters.cronologia_a}
                    onChange={(e) => setFilters({ ...filters, cronologia_a: e.target.value, page: 1 })}
                  />
                </Grid>
                <Grid size={3}>
                  <ACVocabolarioMulti
                    label={t("fondo.genere")}
                    vocabolario="fondo_genere"
                    margin="dense"
                    variant="outlined"
                    fullWidth
                    value={uiFilters.genere as number[]}
                    onChange={(value) => {
                      setFilters({ ...filters, genere: value, page: 1 })
                    }}
                  />
                </Grid>
                <Grid size={3}>
                  <ACVocabolario
                    label={t("supporto.tipo_supporto")}
                    vocabolario="object_tipologis"
                    margin="dense"
                    variant="outlined"
                    fullWidth
                    value={uiFilters.tipo_supporto ? Number(uiFilters.tipo_supporto) : undefined}
                    onChange={(value) => {
                      setFilters({ ...filters, tipo_supporto: value ? String(value) : "", page: 1 })
                    }}
                  />
                </Grid>
                <Grid size={3}>
                  <ACVocabolario
                    label={t("supporto.audio")}
                    vocabolario="sonoro"
                    margin="dense"
                    variant="outlined"
                    fullWidth
                    value={uiFilters.audio ? Number(uiFilters.audio) : undefined}
                    onChange={(value) => {
                      setFilters({ ...filters, audio: value ? String(value) : "", page: 1 })
                    }}
                  />
                </Grid>
              </Grid>
            </CustomToolbar>
            <div
              style={{
                flexGrow: 1,
                overflow: "scroll",
                height: tableHeight ?? undefined,
              }}
              ref={ref}
            >
              {height && (
                <DataGrid
                  disableColumnMenu
                  disableColumnResize
                  // density="compact"
                  pageSizeOptions={PAGE_SIZE_OPTIONS}
                  paginationModel={{ page: 0, pageSize }}
                  sx={{
                    padding: 2,
                    "&.MuiDataGrid-root": {
                      border: "none",
                    },
                  }}
                  localeText={{
                    noRowsLabel: t("general.nessun_dato_disponibile"),
                  }}
                  loading={isLoading}
                  slots={{
                    footer: () => (
                      <Paginator
                        numPages={numPages}
                        page={filters.page}
                        setPage={(page) =>
                          setFilters({
                            ...filters,
                            page,
                          })
                        }
                      />
                    ),
                    toolbar: null,
                  }}
                  rows={data?.results ?? ([] as GridRowsProp)}
                  columns={columns}
                  onSortModelChange={(model) => {
                    if (model.length > 0) {
                      const sortSign = model[0].sort === "asc" ? "" : "-"
                      const newOrdering = `${sortSign}${model[0].field}`
                      setFilters({ ordering: newOrdering, page: 1 })
                    }
                  }}
                />
              )}
            </div>
          </Paper>
        </Box>
      </Stack>

      <DialogForm<Supporto>
        open={createModalState.isOpen}
        handleClose={createModalActions.close}
        title={t("supporto.cta_aggiungi")}
        FormComponent={SupportoForm}
        formComponentProps={{
          fondoId: fondoId ? String(fondoId) : undefined,
        }}
        save={(values: Supporto) => {
          return create(values).then((response) => {
            createModalActions.close()
            if (!fondoId) {
              navigate(`/${i18n.language}/supporti/${response.id}`)
            }
          })
        }}
      />
      <DialogConfirm
        open={deleteModalState.isOpen}
        handleClose={deleteModalActions.close}
        title={t("supporto.cta_elimina")}
        subtitle={
          <span>
            {t("supporto.cta_subtitle_elimina")} <b>"{deleteModalState.value?.segnatura}"</b>&nbsp;?
          </span>
        }
        textConfirm={t("general.elimina")}
        onConfirm={() => {
          return remove(deleteModalState.value.id)
            .then(() => {
              deleteModalActions.close()
              showMessage(t("supporto.esito_elimina_ok"))
            })
            .catch(() => {
              deleteModalActions.close()
              showMessage(t("general.esito_elimina_errore"), "error")
            })
        }}
      />
      <SupportiImport open={importModalState.isOpen} onClose={importModalActions.close} />
    </Layout>
  )
}
